/**
 * useKsefWizard — Hook zarządzający stanem wizarda.
 *
 * Pamięta:
 *  - currentStep (1-4)
 *  - czy każdy krok został ukończony (completedSteps Set)
 *
 * Funkcje:
 *  - goNext(): przechodzi do następnego kroku (jeśli current jest completed)
 *  - goBack(): cofa o jeden krok
 *  - markCompleted(step): oznacza krok jako ukończony
 *  - canProceed: czy bieżący krok jest completed (czyli czy "Dalej" jest aktywne)
 */
import { useState, useCallback, useMemo } from "react";

export const useKsefWizard = (initialStep = 1) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const markCompleted = useCallback((step) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.add(step);
      return next;
    });
  }, []);

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, 4));
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 1));
  }, []);

  const goTo = useCallback((step) => {
    if (step >= 1 && step <= 4) setCurrentStep(step);
  }, []);

  const canProceed = useMemo(
    () => completedSteps.has(currentStep),
    [completedSteps, currentStep]
  );

  return {
    currentStep,
    completedSteps,
    canProceed,
    goNext,
    goBack,
    goTo,
    markCompleted,
  };
};

export default useKsefWizard;
